import API from "@zillow/mortgage-api-client";
import { ABBREVIATIONS_TO_FULL_NAMES } from "../../node_modules/@zillow/state-region-id-map/dist/state-region-id-map.esm.esm.js";
import PropTypes from "prop-types";
import { useLastValidValues } from "../../utils/form/useLastValidValues.esm.js";
import { PAGE_FLAVOR_LOAN_TYPE_MAP } from "../../utils/mortgagehelpers/constants.esm.js";
import { LOAN_TYPES, LOAN_SIZES } from "../MortgageRatesTable/MortgageRatesTable.esm.js";
import { STATE_NAMES, LOAN_PROGRAMS } from "./constants.esm.js";
const OPTIONS_LOAN_TYPE = {
  Purchase: "Purchase",
  Refinance: "Refinance"
};
const CREDIT_SCORE_BUCKETS = {
  VeryHigh: "740 or higher",
  High: "680 - 740",
  Low: "Less than 680"
};
const OPTIONS_LTV = {
  Normal: "20% or higher",
  High: "5% - 20%",
  VeryHigh: "Less than 5%"
};
const OPTIONS_TIMESPAN = {
  OneDay: "1",
  SevenDays: "7",
  OneMonth: "30",
  ThreeMonths: "90",
  OneYear: "365",
  TwoYears: "730"
};
const FieldConfig = {
  Fixed30Year: {
    name: "Fixed30Year"
  },
  Fixed20Year: {
    name: "Fixed20Year"
  },
  Fixed15Year: {
    name: "Fixed15Year"
  },
  Fixed10Year: {
    name: "Fixed10Year"
  },
  ARM7: {
    name: "ARM7"
  },
  ARM5: {
    name: "ARM5"
  },
  ARM3: {
    name: "ARM3"
  },
  loanPurpose: {
    name: "loanPurpose",
    options: [{
      value: "Purchase",
      label: "Purchase"
    }, {
      value: "Refinance",
      label: "Refinance"
    }],
    config: {
      validate: {
        invalidProgram: (value) => {
          let error = null;
          if (Object.values(OPTIONS_LOAN_TYPE).includes(value) === false) {
            error = "Invalid loan purpose";
          }
          return error;
        }
      }
    }
  },
  creditRating: {
    name: "creditRating",
    options: [{
      value: CREDIT_SCORE_BUCKETS.VeryHigh,
      label: CREDIT_SCORE_BUCKETS.VeryHigh
    }, {
      value: CREDIT_SCORE_BUCKETS.High,
      label: CREDIT_SCORE_BUCKETS.High
    }, {
      value: CREDIT_SCORE_BUCKETS.Low,
      label: CREDIT_SCORE_BUCKETS.Low
    }],
    config: {
      validate: {
        invalidProgram: (value) => {
          let error = null;
          if (Object.values(CREDIT_SCORE_BUCKETS).includes(value) === false) {
            error = "Invalid credit rating";
          }
          return error;
        }
      }
    }
  },
  percentDown: {
    name: "percentDown",
    options: [{
      value: OPTIONS_LTV.Normal,
      label: OPTIONS_LTV.Normal
    }, {
      value: OPTIONS_LTV.High,
      label: OPTIONS_LTV.High
    }, {
      value: OPTIONS_LTV.VeryHigh,
      label: OPTIONS_LTV.VeryHigh
    }],
    config: {
      validate: {
        invalidProgram: (value) => {
          let error = null;
          if (Object.values(OPTIONS_LTV).includes(value) === false) {
            error = "Invalid percent down option";
          }
          return error;
        }
      }
    }
  },
  location: {
    name: "location",
    options: STATE_NAMES.map((i) => ({
      value: i.toString(),
      label: i.toString()
    })),
    config: {
      validate: {
        invalidProgram: (value) => {
          let error = null;
          if (STATE_NAMES.includes(value) === false) {
            error = "Invalid location";
          }
          return error;
        }
      }
    }
  },
  timeSpan: {
    name: "timeSpan",
    options: [{
      value: OPTIONS_TIMESPAN.OneDay,
      label: "1 day"
    }, {
      value: OPTIONS_TIMESPAN.SevenDays,
      label: "7 days"
    }, {
      value: OPTIONS_TIMESPAN.OneMonth,
      label: "1 month"
    }, {
      value: OPTIONS_TIMESPAN.ThreeMonths,
      label: "3 months"
    }, {
      value: OPTIONS_TIMESPAN.OneYear,
      label: "1 year"
    }, {
      value: OPTIONS_TIMESPAN.TwoYears,
      label: "2 years"
    }],
    config: {
      validate: {
        invalidProgram: (value) => {
          let error = null;
          if (Object.values(OPTIONS_TIMESPAN).includes(value) === false) {
            error = "Invalid timespan";
          }
          return error;
        }
      }
    }
  }
};
({
  loanPurpose: PropTypes.string,
  creditRating: PropTypes.string,
  percentDown: PropTypes.string,
  location: PropTypes.string,
  timeSpan: PropTypes.string
});
function useSyncFetchHistory(form, flavor) {
  useLastValidValues(form, useSyncGenerateQuery);
  return getApiValuesFromFields(form.getValues(), flavor);
}
function useSyncGenerateQuery(lastValidFields) {
  return lastValidFields;
}
function getKeyByValue(object, value) {
  return Object.keys(object).find((key) => object[key] === value);
}
function getApiValuesFromFields(currentFieldValues, flavor) {
  const queries = {};
  const loanProgramFields = [];
  const checkedLoanProgramFields = [];
  const loanType = PAGE_FLAVOR_LOAN_TYPE_MAP[flavor] ?? LOAN_TYPES.Conventional;
  const loanPrograms = Object.entries(currentFieldValues);
  for (let i = 0, l1 = loanPrograms.length; i < l1; i++) {
    LOAN_PROGRAMS[loanPrograms[i][0]] ? loanProgramFields.push(loanPrograms[i]) : null;
  }
  for (let i = 0, l1 = loanProgramFields.length; i < l1; i++) {
    loanProgramFields[i][1] == true ? checkedLoanProgramFields.push(loanProgramFields[i][0]) : null;
  }
  const location = getKeyByValue(ABBREVIATIONS_TO_FULL_NAMES, currentFieldValues == null ? void 0 : currentFieldValues.location);
  const loanToValueBucket = getKeyByValue(OPTIONS_LTV, currentFieldValues.percentDown);
  const creditScoreBucket = getKeyByValue(CREDIT_SCORE_BUCKETS, currentFieldValues.creditRating);
  checkedLoanProgramFields == null ? void 0 : checkedLoanProgramFields.forEach((program) => {
    var _a;
    queries[program] = {
      stateAbbreviation: location,
      program,
      loanToValueBucket,
      creditScoreBucket,
      refinance: ((_a = currentFieldValues == null ? void 0 : currentFieldValues.loanPurpose) == null ? void 0 : _a.refinance) ? true : false,
      loanType
    };
  });
  return {
    location,
    loanToValueBucket,
    creditScoreBucket,
    queries,
    currentFieldValues,
    checkedLoanProgramFields
  };
}
async function fetchApiResponse(inputValues) {
  var _a;
  try {
    return await ((_a = API) == null ? void 0 : _a.MAX.GET({
      action: "getRates",
      input: {
        partnerId: API.partnerId ?? "RD-WXDQSGN",
        queries: inputValues.queries,
        durationDays: inputValues.currentFieldValues.timeSpan
      }
    }));
  } catch (e) {
    return e.message;
  }
}
const formatTableLabel = ({
  stateAbbreviation,
  durationDays,
  loanAmountBucket,
  loanToValueBucket,
  creditScoreBucket,
  program,
  refinance
}) => {
  const refinanceLabel = refinance ? "refinance" : "purchase";
  const durationLabel = typeof durationDays === "number" ? `${durationDays} days of` : "";
  const loanAmountLabel = `${LOAN_SIZES[loanAmountBucket]} amount`;
  const loanToValueLabel = `${OPTIONS_LTV[loanToValueBucket]} loan to value`;
  const creditScoreLabel = `${CREDIT_SCORE_BUCKETS[creditScoreBucket]} credit score`;
  const programLabel = `${LOAN_PROGRAMS[program]}`;
  const stateLabel = `${stateAbbreviation || "US"}`;
  return `${programLabel} ${durationLabel} ${refinanceLabel} rates for someone with a ${creditScoreLabel} and a ${loanToValueLabel} planning to borrow a ${loanAmountLabel} in the ${stateLabel} region.`;
};
const DAY_FORMAT = "PPP";
const HOUR_FORMAT = "PPPppp";
const getFormatDate = (formatDate) => (date, duration) => formatDate(new Date(date), duration < 30 ? HOUR_FORMAT : DAY_FORMAT);
export {
  CREDIT_SCORE_BUCKETS,
  DAY_FORMAT,
  FieldConfig,
  HOUR_FORMAT,
  OPTIONS_LOAN_TYPE,
  OPTIONS_LTV,
  OPTIONS_TIMESPAN,
  fetchApiResponse,
  formatTableLabel,
  getApiValuesFromFields,
  getFormatDate,
  useSyncFetchHistory,
  useSyncGenerateQuery
};
